/**
 * Fase 5 — Instellingen (owner-only bedrijfsinstellingen + persoonlijke voorkeuren).
 *
 * Sectie-indeling:
 *   1. Bedrijf       — naam, sector, industry_other bij Anders
 *   2. Lokalisatie   — land (ISO combobox), tijdzone (IANA select), taal
 *   3. Medewerkers   — read-only seat-teller uit /companies/me (bestaand contract)
 *   4. Privacy       — bestaande legacy toggles + Privacy-Matrix (ongewijzigd)
 *   5. Uitnodigingen — bestaand invite gedrag + expiry dagen
 *   6. Weergave      — thema (delegated to Profile op meta-niveau)
 *
 * Server-side blijft leidend voor RBAC en validatie; frontend disabled inputs
 * spiegelen alleen de backend contracten uit `deps.py` / `routes/companies.py`.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import { COUNTRIES, listTimezones, tzLabel } from "@/data/catalog";
import {
  Save, Building2, ShieldCheck, MailCheck, Cake, Phone, AtSign, Building,
  Briefcase, Users as UsersIcon, Globe, Search, Check,
} from "lucide-react";
import { toast } from "sonner";
import GdprSection from "@/components/GdprSection";
// Phase 9B.2.1 — SecuritySection is verplaatst naar /profile zodat alle
// rollen hun eigen MFA + email-verification kunnen beheren. Deze pagina
// bevat alleen company-brede instellingen.

const INDUSTRIES = ["hospitality", "retail", "healthcare", "office", "logistics", "other"];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { t, lang } = useI18n();
  const [company, setCompany] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busyBiz, setBusyBiz] = useState(false);
  const [busySet, setBusySet] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");
  // Fase 6.5B post-6 — personal preference (default true, server-side canonical).
  const [welcomePref, setWelcomePref] = useState(user?.show_login_welcome_summary !== false);
  const [busyWelcome, setBusyWelcome] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/companies/me"),
          api.get("/company/settings"),
        ]);
        setCompany(c.data);
        setSettings(s.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, []);

  // Note: hooks below must run every render (even when data is still loading)
  // to keep hook order stable — the loading return happens AFTER them.
  const countryList = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    const idx = lang === "en" ? 2 : 1;
    let list = [...COUNTRIES].sort((a, b) => a[idx].localeCompare(b[idx], lang));
    if (q) list = list.filter(([code, nl, en]) => code.toLowerCase().includes(q) || nl.toLowerCase().includes(q) || en.toLowerCase().includes(q));
    return list;
  }, [countryQuery, lang]);
  const timezones = useMemo(() => listTimezones().sort(), []);

  if (!company || !settings) return <div className="skeleton h-96 rounded-3xl" />;

  const isOwner = user?.role === "owner";

  const saveBusiness = async () => {
    setBusyBiz(true);
    try {
      const payload = {
        name: company.name,
        industry: company.industry,
        industry_other: company.industry === "other" ? (company.industry_other || "") : null,
        country: company.country,
        timezone: company.timezone,
        language: company.language,
        size: company.size,
      };
      const { data } = await api.patch("/companies/me", payload);
      setCompany(data);
      toast.success(t("company_saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyBiz(false); }
  };
  const saveSettings = async () => {
    setBusySet(true);
    try {
      const { data } = await api.patch("/company/settings", settings);
      setSettings(data);
      toast.success(t("settings_saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusySet(false); }
  };
  const upd = (k) => (v) => setSettings((s) => ({ ...s, [k]: v }));

  const seatUsage = company.seat_usage || {};
  const seatMax = seatUsage.max;
  const seatText = seatMax == null
    ? t("seats_unlimited")
    : `${seatUsage.current || 0} / ${seatMax} ${t("employees").toLowerCase()}`;

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="page-settings">
      <PageHeader
        eyebrow={t("admin_label")}
        title={t("settings")}
        subtitle={isOwner ? t("settings_owner_desc") : t("settings_readonly")}
      />

      {/* -------- Persoonlijke voorkeuren -------- */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card p-6 md:p-8" data-testid="section-personal-prefs">
        <SectionHead icon={ShieldCheck} title={t("settings_welcome_summary_label")} desc={t("settings_welcome_summary_description")} />
        <div className="mt-6">
          <Toggle
            icon={ShieldCheck}
            label={t("settings_welcome_summary_label")}
            hint={t("settings_welcome_summary_description")}
            value={welcomePref}
            testid="toggle-welcome-summary"
            onChange={async (v) => {
              setBusyWelcome(true); setWelcomePref(v);
              try {
                await api.patch("/users/me/welcome-summary-pref", { show_login_welcome_summary: v });
                if (refreshUser) await refreshUser();
                toast.success(t("settings_saved"));
              } catch (e) {
                setWelcomePref(!v);
                toast.error(formatApiErrorDetail(e.response?.data?.detail));
              } finally { setBusyWelcome(false); }
            }}
            disabled={busyWelcome}
          />
        </div>
      </motion.section>

      {/* -------- Bedrijf -------- */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card p-6 md:p-8" data-testid="section-company">
        <SectionHead icon={Building2} title={t("company_data")} desc={t("company_data_desc")} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <Field label={t("company_name")}>
            <input data-testid="set-company-name" disabled={!isOwner} value={company.name || ""}
                   onChange={(e) => setCompany({ ...company, name: e.target.value })} className="input-field" />
          </Field>
          <Field label={t("sector")}>
            <select data-testid="set-company-industry" disabled={!isOwner} value={company.industry || "other"}
                    onChange={(e) => setCompany({ ...company, industry: e.target.value })} className="input-field">
              {INDUSTRIES.map((k) => (
                <option key={k} value={k}>{t(`industry_${k}`)}</option>
              ))}
            </select>
          </Field>
          {company.industry === "other" && (
            <Field label={t("industry_other_label")}>
              <input
                data-testid="set-company-industry-other"
                disabled={!isOwner}
                value={company.industry_other || ""}
                maxLength={60}
                onChange={(e) => setCompany({ ...company, industry_other: e.target.value })}
                className="input-field"
                placeholder={t("industry_other_placeholder")}
              />
            </Field>
          )}
          <Field label={t("default_language")}>
            <select data-testid="set-company-lang" disabled={!isOwner} value={company.language || "nl"}
                    onChange={(e) => setCompany({ ...company, language: e.target.value })} className="input-field">
              {["nl", "en"].map((k) => <option key={k} value={k}>{k.toUpperCase()}</option>)}
            </select>
          </Field>
        </div>
        {isOwner && (
          <div className="mt-6 flex justify-end">
            <button onClick={saveBusiness} disabled={busyBiz} className="btn btn-primary" data-testid="set-company-save">
              <Save size={14} /> {busyBiz ? t("saving") : t("save")}
            </button>
          </div>
        )}
      </motion.section>

      {/* -------- Lokalisatie -------- */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }} className="card p-6 md:p-8" data-testid="section-locale">
        <SectionHead icon={Globe} title={t("localisation")} desc={t("localisation_desc")} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <Field label={t("country")}>
            {/* Combobox: sync text filter for large ISO list. Native <select>
                keeps mobile UX + keyboard predictable. */}
            <div className="relative">
              <div className="flex items-center gap-2 mb-1.5">
                <Search size={12} className="text-[hsl(var(--text-muted))]" />
                <input
                  data-testid="set-country-search"
                  placeholder={t("country_search_placeholder")}
                  value={countryQuery}
                  onChange={(e) => setCountryQuery(e.target.value)}
                  className="input-field !h-8 flex-1 text-[12px]"
                  disabled={!isOwner}
                />
              </div>
              <select
                data-testid="set-company-country"
                disabled={!isOwner}
                value={company.country || "NL"}
                onChange={(e) => setCompany({ ...company, country: e.target.value })}
                className="input-field"
                size={5}
              >
                {countryList.map(([code, nl, en]) => (
                  <option key={code} value={code}>{lang === "en" ? en : nl} · {code}</option>
                ))}
              </select>
            </div>
          </Field>
          <Field label={t("timezone_label")}>
            <select
              data-testid="set-company-tz"
              disabled={!isOwner}
              value={company.timezone || "Europe/Amsterdam"}
              onChange={(e) => setCompany({ ...company, timezone: e.target.value })}
              className="input-field"
            >
              {timezones.map((tz) => (<option key={tz} value={tz}>{tzLabel(tz)}</option>))}
            </select>
          </Field>
        </div>
        {isOwner && (
          <div className="mt-6 flex justify-end">
            <button onClick={saveBusiness} disabled={busyBiz} className="btn btn-primary" data-testid="set-locale-save">
              <Save size={14} /> {busyBiz ? t("saving") : t("save")}
            </button>
          </div>
        )}
      </motion.section>

      {/* -------- Medewerkers & abonnement -------- */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }} className="card p-6 md:p-8" data-testid="section-seats">
        <SectionHead icon={UsersIcon} title={t("employees_and_plan")} desc={t("employees_and_plan_desc")} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <ReadonlyField label={t("seat_count")} testid="seat-count-value">
            <span className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))] tabular-nums">{seatText}</span>
            {seatMax != null && seatUsage.remaining != null && (
              <span className="ml-2 text-[12px] text-[hsl(var(--text-muted))]">· {seatUsage.remaining} {t("seats_available")}</span>
            )}
          </ReadonlyField>
          <ReadonlyField label={t("company_size_label")} testid="company-size-value">
            <span className="font-display text-[15px] text-[hsl(var(--text-strong))]">
              {company.size || "1-10"}
            </span>
            <span className="ml-2 text-[11px] text-[hsl(var(--text-muted))]">· {t("company_size_hint")}</span>
          </ReadonlyField>
        </div>
      </motion.section>

      {/* -------- Privacy -------- */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.09 }} className="card p-6 md:p-8" data-testid="section-privacy">
        <SectionHead icon={ShieldCheck} title={t("privacy_label")} desc={t("privacy_desc")} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
          <Toggle disabled={!isOwner} testid="tog-phone" icon={Phone} label={t("show_phone")} value={settings.show_phone_to_employees} onChange={upd("show_phone_to_employees")} />
          <Toggle disabled={!isOwner} testid="tog-email" icon={AtSign} label={t("show_email")} value={settings.show_email_to_employees} onChange={upd("show_email_to_employees")} />
          <Toggle disabled={!isOwner} testid="tog-dep" icon={Building} label={t("show_dept")} value={settings.show_department_to_employees} onChange={upd("show_department_to_employees")} />
          <Toggle disabled={!isOwner} testid="tog-title" icon={Briefcase} label={t("show_title")} value={settings.show_job_title_to_employees} onChange={upd("show_job_title_to_employees")} />
          <Toggle disabled={!isOwner} testid="tog-bday" icon={Cake} label={t("show_birthday")} hint={t("show_birthday_hint")}
                  value={settings.show_birthday_to_employees} onChange={upd("show_birthday_to_employees")} />
          <Toggle disabled={!isOwner} testid="tog-year" icon={Cake} label={t("hide_birthyear")} hint={t("hide_birthyear_hint")}
                  value={settings.hide_birth_year} onChange={upd("hide_birth_year")} />
          <Toggle disabled={!isOwner} testid="tog-emp-photo" icon={Cake} label={t("allow_emp_photo")} hint={t("allow_emp_photo_hint")}
                  value={!!settings.allow_employee_profile_photo} onChange={upd("allow_employee_profile_photo")} />
        </div>

        <div className="mt-8" data-testid="privacy-matrix">
          <div className="text-[13px] font-bold text-[hsl(var(--text-strong))] mb-1">{t("privacy_matrix_title")}</div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] mb-3">{t("privacy_matrix_desc")}</div>
          <div className="overflow-x-auto border hairline rounded-2xl">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="bg-[hsl(var(--bg))]">
                  <th className="text-left px-4 py-2.5 font-semibold text-[hsl(var(--text-muted))]">{t("field")}</th>
                  {["manager", "teamlead", "employee"].map((r) => (
                    <th key={r} className="text-center px-4 py-2.5 font-semibold text-[hsl(var(--text-muted))]">{t(`role_${r}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["phone",         t("privacy_field_phone")],
                  ["email",         t("privacy_field_email")],
                  ["date_of_birth", t("privacy_field_dob")],
                  ["job_title",     t("privacy_field_title")],
                  ["department",    t("privacy_field_dep")],
                  ["avatar_url",    t("privacy_field_photo")],
                ].map(([field, label]) => (
                  <tr key={field} className="border-t hairline">
                    <td className="px-4 py-2.5 font-medium text-[hsl(var(--text-strong))]">{label}</td>
                    {["manager", "teamlead", "employee"].map((role) => {
                      const cur = ((settings.privacy_matrix || {})[role] || {});
                      const val = field in cur ? !!cur[field] : true;
                      return (
                        <td key={role} className="px-4 py-2.5 text-center">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" disabled={!isOwner} data-testid={`pm-${role}-${field}`} checked={val}
                              onChange={(e) => {
                                const next = { ...(settings.privacy_matrix || {}),
                                  [role]: { ...((settings.privacy_matrix || {})[role] || {}), [field]: e.target.checked } };
                                upd("privacy_matrix")(next);
                              }}
                              className="w-4 h-4 accent-[hsl(var(--primary))]" />
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.section>

      {/* -------- Uitnodigingen -------- */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="card p-6 md:p-8" data-testid="section-invitations">
        <SectionHead icon={MailCheck} title={t("invitations")} desc={t("invitations_desc")} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
          <Toggle disabled={!isOwner} testid="tog-mgr"  icon={UsersIcon} label={t("managers_invite")}  value={settings.allow_managers_to_invite}  onChange={upd("allow_managers_to_invite")} />
          <Toggle disabled={!isOwner} testid="tog-lead" icon={UsersIcon} label={t("teamleads_invite")} value={settings.allow_teamleads_to_invite} onChange={upd("allow_teamleads_to_invite")} />
          <Field label={t("codes_expire")}>
            <input data-testid="set-expiry-days" disabled={!isOwner} type="number" min={1} max={90}
                   value={settings.invitation_expiry_days || 7}
                   onChange={(e) => upd("invitation_expiry_days")(parseInt(e.target.value, 10) || 7)}
                   className="input-field" />
            <span className="text-[11px] text-[hsl(var(--text-muted))] mt-1 inline-block">{t("codes_expire_hint")}</span>
          </Field>
        </div>
        {isOwner && (
          <div className="mt-6 flex justify-end">
            <button onClick={saveSettings} disabled={busySet} className="btn btn-primary" data-testid="set-settings-save">
              <Save size={14} /> {busySet ? t("saving") : t("save_privacy_invitations")}
            </button>
          </div>
        )}
      </motion.section>

      {/* -------- Weergave / Persoonlijk pointer -------- */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="card p-6 md:p-8" data-testid="section-personal">
        <SectionHead icon={Check} title={t("personal_settings")} desc={t("personal_settings_desc")} />
        <div className="text-[13px] text-[hsl(var(--text-muted))] mt-2">{t("personal_settings_link_hint")}</div>
      </motion.section>

      {/* -------- Fase 6 — Privacy & gegevens -------- */}
      <GdprSection />
      {/* Phase 9B.2.1 — persoonlijke Security is verplaatst naar /profile
          zodat alle rollen (owner/manager/teamlead/employee) hun eigen MFA
          en email-verification kunnen beheren. Deze pagina bevat alleen
          company-brede instellingen. */}
    </div>
  );
}

function SectionHead({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
        <Icon size={16} strokeWidth={1.75} />
      </div>
      <div>
        <div className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))]">{title}</div>
        <div className="text-[13px] text-[hsl(var(--text-muted))] mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{label}</span>
      {children}
    </label>
  );
}

function ReadonlyField({ label, children, testid }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{label}</span>
      <div data-testid={testid} className="input-field flex items-center bg-[hsl(var(--muted))] !text-[hsl(var(--text-strong))]">{children}</div>
    </div>
  );
}

function Toggle({ icon: Icon, label, value, onChange, hint, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value ? "true" : "false"}
      aria-label={label}
      onClick={() => !disabled && onChange(!value)}
      data-testid={testid}
      disabled={disabled}
      className={`flex items-center gap-3 rounded-2xl border hairline p-4 text-left transition
        ${value ? "bg-[hsl(var(--primary-soft))] border-[hsl(var(--primary))]" : "bg-white hover:bg-[hsl(var(--muted))]"}
        ${disabled ? "opacity-60 cursor-not-allowed" : ""}
        focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-2`}
    >
      <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0
        ${value ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"}`}>
        <Icon size={15} strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{label}</div>
        {hint && <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">{hint}</div>}
      </div>
      <span className={`inline-flex w-9 h-5 rounded-full transition items-center
        ${value ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]"}`}>
        <span className={`w-4 h-4 rounded-full bg-white transition-transform mx-0.5
          ${value ? "translate-x-4" : "translate-x-0"}`} />
      </span>
    </button>
  );
}
